import { inject, Injectable } from '@angular/core';
import { NotificationLog, NotificationLogsService, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { PermissionsService } from '../../../services/permissions.service';
import { SessionService } from '../../../services/session.service';

/**
 * Server-Fakten zum Portal-Zugang EINER Rolle. Welcher Fall daraus wird, entscheidet die Oberfläche —
 * nur sie kennt den aktuellen Inhalt des E-Mail-Feldes.
 */
export interface RolePortalAccess {
	/** Ein Code dieser Rolle wurde eingelöst: die Rolle ist im Portal registriert. */
	registered: boolean;
	/** Es gibt mindestens einen noch nicht eingelösten Zugangscode. */
	hasOpenCode: boolean;
	/**
	 * Der offene Zugangscode, um den es geht. Wird er beim Speichern mitgegeben, verschickt der
	 * Auth-Service DIESEN Code erneut; ohne ihn entstünde ein neuer und der alte (Ausdruck, QR-Zettel)
	 * würde entwertet.
	 */
	openCode?: string;
	/** Letzter Mailversand eines offenen Codes; fehlt, wenn nie eine Mail rausging. */
	lastNotifiedOn?: string;
	/** Adresse, an die der Code zuletzt ging — verrät eine seither geänderte E-Mail-Adresse. */
	lastNotifiedEmail?: string;
}

export const NO_PORTAL_ACCESS: RolePortalAccess = { registered: false, hasOpenCode: false };

/**
 * Ermittelt je Rolle, wie es um deren araWeb-/araApp-Zugang steht.
 *
 * Berechtigungen hängen an der Rollen-Id (`permissionUserId`), deshalb braucht das Lesen keinen
 * `PermissionType`. „Mail ist rausgegangen" steht nicht am Zugangscode selbst, sondern nur in den
 * Notification-Logs — die gibt es ausschließlich je `authCode`, ein Bulk-Endpunkt existiert nicht.
 *
 * Fehler werden NICHT verschluckt: aus einer halben Antwort würde die Oberfläche sonst einen falschen
 * Fall ableiten und z. B. „Code erneut versenden" anbieten, wo noch nie einer verschickt wurde.
 */
@Injectable({ providedIn: 'root' })
export class RolePortalAccessService {
	private readonly permissionsService = inject(PermissionsService);
	private readonly notificationLogsService = inject(NotificationLogsService);
	private readonly sessionService = inject(SessionService);

	load(roleId: string): Observable<RolePortalAccess> {
		return this.permissionsService.getAllPermissionsForPermissionUser(roleId).pipe(switchMap((permissions) => this.buildAccess(permissions ?? [])));
	}

	/** Mehrere Rollen auf einmal — eine Person kann Eigentümer und Bewohner derselben Einheit sein. */
	loadMany(roleIds: readonly string[]): Observable<Map<string, RolePortalAccess>> {
		const uniqueIds = [...new Set(roleIds.filter((roleId) => !!roleId))];
		if (uniqueIds.length === 0) {
			return of(new Map<string, RolePortalAccess>());
		}
		return forkJoin(uniqueIds.map((roleId) => this.load(roleId))).pipe(map((states) => new Map(uniqueIds.map((roleId, index) => [roleId, states[index]]))));
	}

	private buildAccess(permissions: PermissionObject[]): Observable<RolePortalAccess> {
		const registered = permissions.some((permission) => !!permission.codeActivatedOn);
		const openCodes = permissions.filter((permission) => !permission.codeActivatedOn && !!permission.authCode);
		if (openCodes.length === 0) {
			return of({ registered, hasOpenCode: false });
		}
		return forkJoin(
			openCodes.map((permission) => this.loadLogs(permission.authCode).pipe(map((logs) => ({ permission, notification: this.newestNotification(logs) }))))
		).pipe(map((codes) => this.pickOpenCode(registered, codes)));
	}

	/**
	 * Bei mehreren offenen Codes gewinnt der zuletzt verschickte — auf den bezieht sich „erneut
	 * versenden". Wurde keiner verschickt, der am längsten gültige.
	 */
	private pickOpenCode(registered: boolean, codes: { permission: PermissionObject; notification?: NotificationLog }[]): RolePortalAccess {
		const notified = codes
			.filter((code) => !!code.notification)
			.sort((a, b) => this.timeOf(b.notification?.notifiedOn) - this.timeOf(a.notification?.notifiedOn));
		const chosen = notified[0] ?? [...codes].sort((a, b) => this.timeOf(b.permission.codeValidUntil) - this.timeOf(a.permission.codeValidUntil))[0];
		return {
			registered,
			hasOpenCode: true,
			openCode: chosen.permission.authCode,
			lastNotifiedOn: chosen.notification?.notifiedOn,
			lastNotifiedEmail: chosen.notification?.email,
		};
	}

	private timeOf(date: string | undefined): number {
		return date ? new Date(date).getTime() : 0;
	}

	private loadLogs(authCode: string): Observable<NotificationLog[]> {
		return this.notificationLogsService.getNotificationLogsByAuthCode(this.sessionService.effectiveArea, authCode).pipe(map((logs) => logs ?? []));
	}

	/** Der jüngste Versand gewinnt: mehrere offene Codes können unterschiedlich oft verschickt worden sein. */
	private newestNotification(logs: NotificationLog[]): NotificationLog | undefined {
		return logs
			.filter((log) => !!log.notifiedOn)
			.sort((a, b) => new Date(b.notifiedOn).getTime() - new Date(a.notifiedOn).getTime())
			.at(0);
	}
}
